import type { Vec2 } from '@vp/core';

/**
 * Editing helpers for bezier paths (pen tool, §5): nodes are flat
 * [x, y, inX, inY, outX, outY]* in the object's local coordinates.
 */
export type NodePart = 'anchor' | 'in' | 'out';

export function nodeCount(nodes: number[]): number {
  return Math.floor(nodes.length / 6);
}

function segCount(nodes: number[], closed: boolean): number {
  const n = nodeCount(nodes);
  return closed && n > 2 ? n : n - 1;
}

/** Control points of segment s: p0, c1, c2, p3. */
function segment(nodes: number[], s: number): [number, number, number, number, number, number, number, number] {
  const n = nodeCount(nodes);
  const a = s * 6;
  const b = ((s + 1) % n) * 6;
  return [nodes[a], nodes[a + 1], nodes[a + 4], nodes[a + 5], nodes[b + 2], nodes[b + 3], nodes[b], nodes[b + 1]];
}

function cubic(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
}

/** Closest point of the path to p: segment index, curve parameter and distance. */
export function nearestOnPath(nodes: number[], closed: boolean, p: Vec2): { seg: number; t: number; dist: number; point: Vec2 } | null {
  let best: { seg: number; t: number; dist: number; point: Vec2 } | null = null;
  const segs = segCount(nodes, closed);
  for (let s = 0; s < segs; s++) {
    const [x0, y0, x1, y1, x2, y2, x3, y3] = segment(nodes, s);
    // coarse sampling, then refine around the best sample
    let bt = 0;
    let bd = Infinity;
    const N = 32;
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const d = Math.hypot(cubic(x0, x1, x2, x3, t) - p.x, cubic(y0, y1, y2, y3, t) - p.y);
      if (d < bd) {
        bd = d;
        bt = t;
      }
    }
    let step = 1 / N;
    for (let k = 0; k < 12; k++) {
      step /= 2;
      for (const t of [bt - step, bt + step]) {
        if (t < 0 || t > 1) continue;
        const d = Math.hypot(cubic(x0, x1, x2, x3, t) - p.x, cubic(y0, y1, y2, y3, t) - p.y);
        if (d < bd) {
          bd = d;
          bt = t;
        }
      }
    }
    if (!best || bd < best.dist) best = { seg: s, t: bt, dist: bd, point: { x: cubic(x0, x1, x2, x3, bt), y: cubic(y0, y1, y2, y3, bt) } };
  }
  return best;
}

/** Insert an anchor on segment `seg` at parameter t without changing the shape (de Casteljau split). */
export function insertNode(nodes: number[], seg: number, t: number): { nodes: number[]; index: number } {
  const n = nodeCount(nodes);
  const [x0, y0, x1, y1, x2, y2, x3, y3] = segment(nodes, seg);
  const lerp = (a: number, b: number) => a + (b - a) * t;
  const ax = lerp(x0, x1), ay = lerp(y0, y1);
  const bx = lerp(x1, x2), by = lerp(y1, y2);
  const cx = lerp(x2, x3), cy = lerp(y2, y3);
  const dx = lerp(ax, bx), dy = lerp(ay, by);
  const ex = lerp(bx, cx), ey = lerp(by, cy);
  const fx = lerp(dx, ex), fy = lerp(dy, ey);
  const out = nodes.slice();
  const a = seg * 6;
  const b = ((seg + 1) % n) * 6;
  const straight = x1 === x0 && y1 === y0 && x2 === x3 && y2 === y3;
  if (straight) {
    // a corner point on a straight segment keeps both halves straight
    const at = (seg + 1) * 6;
    out.splice(at, 0, fx, fy, fx, fy, fx, fy);
    return { nodes: out, index: seg + 1 };
  }
  out[a + 4] = ax;
  out[a + 5] = ay;
  out[b + 2] = cx;
  out[b + 3] = cy;
  const at = (seg + 1) * 6;
  out.splice(at, 0, fx, fy, dx, dy, ex, ey);
  return { nodes: out, index: seg + 1 };
}

export function removeNode(nodes: number[], index: number): number[] {
  const out = nodes.slice();
  out.splice(index * 6, 6);
  return out;
}

/** Topmost editable part under p: handles of the selected node and its neighbours first, then anchors. */
export function hitNodePart(nodes: number[], p: Vec2, tol: number, selected: number): { index: number; part: NodePart } | null {
  const n = nodeCount(nodes);
  const near = (x: number, y: number) => Math.hypot(x - p.x, y - p.y) <= tol;
  for (const i of [selected, selected - 1, selected + 1]) {
    if (i < 0 || i >= n) continue;
    const b = i * 6;
    if ((nodes[b + 4] !== nodes[b] || nodes[b + 5] !== nodes[b + 1]) && near(nodes[b + 4], nodes[b + 5])) return { index: i, part: 'out' };
    if ((nodes[b + 2] !== nodes[b] || nodes[b + 3] !== nodes[b + 1]) && near(nodes[b + 2], nodes[b + 3])) return { index: i, part: 'in' };
  }
  for (let i = n - 1; i >= 0; i--) if (near(nodes[i * 6], nodes[i * 6 + 1])) return { index: i, part: 'anchor' };
  return null;
}

/**
 * Drag one part of a node to local point l (positions taken from `orig`).
 * anchor: moves with its handles; handle: the opposite one stays colinear keeping its length
 * (unless `breakHandles`, or the node is a corner); convert: pulls fresh symmetric handles.
 */
export function dragPart(orig: number[], index: number, part: NodePart | 'convert', l: Vec2, start: Vec2, breakHandles: boolean): number[] {
  const nodes = orig.slice();
  const b = index * 6;
  const x = nodes[b];
  const y = nodes[b + 1];
  if (part === 'anchor') {
    const dx = l.x - start.x;
    const dy = l.y - start.y;
    for (const k of [0, 2, 4]) {
      nodes[b + k] += dx;
      nodes[b + k + 1] += dy;
    }
    return nodes;
  }
  if (part === 'convert') {
    nodes[b + 4] = l.x;
    nodes[b + 5] = l.y;
    nodes[b + 2] = 2 * x - l.x;
    nodes[b + 3] = 2 * y - l.y;
    return nodes;
  }
  const self = part === 'out' ? 4 : 2;
  const other = part === 'out' ? 2 : 4;
  nodes[b + self] = l.x;
  nodes[b + self + 1] = l.y;
  const ox = orig[b + other] - x;
  const oy = orig[b + other + 1] - y;
  const len = Math.hypot(ox, oy);
  if (!breakHandles && len > 1e-9) {
    const vx = l.x - x;
    const vy = l.y - y;
    const vl = Math.hypot(vx, vy);
    if (vl > 1e-9) {
      nodes[b + other] = x - (vx / vl) * len;
      nodes[b + other + 1] = y - (vy / vl) * len;
    }
  }
  return nodes;
}

/** Collapse both handles onto the anchor (a sharp corner). */
export function makeCorner(orig: number[], index: number): number[] {
  const nodes = orig.slice();
  const b = index * 6;
  nodes[b + 2] = nodes[b + 4] = nodes[b];
  nodes[b + 3] = nodes[b + 5] = nodes[b + 1];
  return nodes;
}
